import { Player, PlayerEvent } from "@lottiefiles/react-lottie-player"
import useTimeoutWhen from "@rooks/use-timeout-when"
import { thankYouLottieWithName } from "assets/lotties/thankYouLottieWithName"
import { useContext, useLayoutEffect, useMemo, useState } from "react"
import styled, { ThemeContext } from "styled-components"

import { useAnimationContext } from "../AnimationContext"

const FADE_DURATION_MS = 500

const FadeContainer = styled.div<{ $showing: boolean }>`
  width: 100%;
  height: 100%;

  display: flex;
  justify-content: center;
  align-items: center;

  background-color: ${(props) =>
    props.$showing ? "#00000030" : "transparent"};
  transition: background-color ${FADE_DURATION_MS}ms ease;
`

const AnimationWrapper = styled.div`
  height: 70%;
  display: flex;
  justify-content: center;
`

export interface ThankYouProps {
  onComplete: () => void
  name: string | null
}

const ThankYou = ({ onComplete, name }: ThankYouProps) => {
  const theme = useContext(ThemeContext)
  const { setThankYouAnimationFirstName } = useAnimationContext()
  const [showingFade, setShowingFade] = useState<boolean>(false)
  const [fadingOut, setFadingOut] = useState<boolean>(false)
  useTimeoutWhen(onComplete, FADE_DURATION_MS, fadingOut)

  // Start fade animation after the first render so we start un-faded
  useLayoutEffect(() => {
    setShowingFade(true)

    // On unmount, reset first name data
    return () => {
      setThankYouAnimationFirstName(null)
    }
  }, [])

  // Memoize animation data so we don't end up restarting the animation every render
  const animationData = useMemo(
    () => thankYouLottieWithName(name ?? "", theme.colors.accent),
    [name],
  )

  // Passed to the lottie animation player. Listens for the completion of the animation
  const onPlayerEvent = (event: PlayerEvent) => {
    if (event === "complete") {
      setShowingFade(false)
      setFadingOut(true)
    }
  }

  return (
    <FadeContainer $showing={showingFade}>
      <AnimationWrapper>
        <Player
          autoplay
          loop={false}
          src={animationData}
          onEvent={onPlayerEvent}
          style={{ height: "100%" }}
        />
      </AnimationWrapper>
    </FadeContainer>
  )
}

export default ThankYou
